import Close16 from '@carbon/icons-react/es/close/16';
import { keyBy } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Account } from 'api/account';
import { RateCardLite, RateCardVersion } from 'api/rate-cards';
import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
} from 'components/atoms/drawer';
import { Form } from 'components/atoms/form';
import { Loading } from 'components/atoms/loading';
import { toastService } from 'components/molecules/toast';
import { useTranslation } from 'hooks/use-translation';
import { useNativeActiveOrInvitedUsersQuery } from 'queries/members';
import {
  useCreateRateCardMutation,
  useRateCardVersionsQuery,
} from 'queries/rate-cards';
import { useRolesQuery } from 'queries/roles';
import { RateCardCreationMeta } from '../types';
import { RateCardsDrawerFooter } from './footer';
import { RateCardsForm } from './form';
import {
  useRateCardVersionCreate,
  useRateCardVersionDelete,
  useRateCardVersionUpdate,
  useRoleRates,
} from './handers';
import { RateCardsDrawerHeader } from './header';
import { RateCardVersionsSider } from './sider';
import { useRateCardUtils } from './utils';

export const SIDER_WIDTH = 320;
const DRAWER_WIDTH = 640;

const LoaderWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledDrawerWithSider = styled.div`
  display: flex;
  height: 100%;
`;

const StyledCustomDrawerContent = styled(CustomLayoutDrawerContent)`
  ${CustomLayoutDrawerContent.Title} {
    padding-left: var(--spacing-06);
  }

  ${CustomLayoutDrawerContent.Content} {
    padding-top: var(--spacing-06);
    padding-left: var(--spacing-06);
    padding-right: var(--spacing-06);
  }

  ${CustomLayoutDrawerContent.Footer} {
    padding: var(--spacing-06);
  }
`;

interface Props {
  isVisible: boolean;
  account: Account;
  selectedRateCard: RateCardLite | undefined;
  isNewRateCardVersion: boolean;
  updateNewRateCardVersion: (flag: boolean) => void;
  onClose: () => void;
}

export const RateCardsDrawer: FC<Props> = ({
  isVisible,
  account,
  selectedRateCard,
  isNewRateCardVersion,
  updateNewRateCardVersion,
  onClose,
}) => {
  const [form] = Form.useForm<RateCardCreationMeta>();

  const { data: roles = [], isLoading: isRolesLoading } = useRolesQuery();
  const { data: users = [], isLoading: isUsersLoading } =
    useNativeActiveOrInvitedUsersQuery();
  const { data: rateCardDetails, isLoading: isRateCardDetailsLoading } =
    useRateCardVersionsQuery(selectedRateCard?.rateCardId);

  const {
    mutateAsync: createRateCardMutation,
    isLoading: isRateCardCreationInProgress,
  } = useCreateRateCardMutation();

  const createSuccessToastTitle = useTranslation(
    'settings.rate_cards.rate_card.create.toast.success.title',
  );
  const createSuccessToastMessage = useTranslation(
    'settings.rate_cards.rate_card.create.toast.success.message',
  );
  const createErrorToastTitle = useTranslation(
    'settings.rate_cards.rate_card.create.toast.error.title',
  );
  const createErrorToastMessage = useTranslation(
    'settings.rate_cards.rate_card.create.toast.error.message',
  );

  const roleLookup = useMemo(() => {
    if (isRolesLoading) {
      return undefined;
    }

    return keyBy(roles, 'roleId');
  }, [isRolesLoading, roles]);

  const userLookup = useMemo(() => {
    if (isUsersLoading) {
      return undefined;
    }

    return keyBy(users, 'userId');
  }, [isUsersLoading, users]);

  const [canSubmit, setCanSubmit] = useState(false);
  const [rateCardVersions, setRateCardVersions] = useState<RateCardVersion[]>(
    [],
  );
  const [selectedRateCardVersion, setSelectedRateCardVersion] =
    useState<RateCardVersion | undefined>(undefined);
  const [isEditMode, setIsEditMode] = useState(false);

  const {
    roleRates,
    resetRoleRate,
    resetToOriginalRoleRates,
    updateRoleRate,
    deleteRoleRate,
  } = useRoleRates({
    account,
    roleLookup,
    selectedRateCardVersion,
  });

  const onUpdateRateCardVersion = useCallback(
    (newVersionId: number) => {
      const newVersion = rateCardVersions.find(
        (version) => version.rateCardVersionId === newVersionId,
      );

      setSelectedRateCardVersion(newVersion);
      setCanSubmit(false);
      setIsEditMode(false);
      setRateCardVersions((prev) =>
        prev.filter(({ rateCardVersionId }) => rateCardVersionId > 0),
      );
    },
    [rateCardVersions],
  );

  const onDrawerClose = useCallback(() => {
    form.resetFields();
    resetRoleRate();
    setCanSubmit(false);
    setIsEditMode(false);
    setRateCardVersions([]);
    onClose();
  }, [form, onClose, resetRoleRate]);

  const {
    isCurrentRateCardVersionReadOnly,
    isUnsavedRateCardVersionPresent,
    createNewRateCardVersion,
    getRateCardPayload,
    getRateCardVersionPayload,
    getActiveOrRecentRateCardVersion,
    updateSelectedRateCardVersion,
    closeDrawerHandler,
  } = useRateCardUtils({
    account,
    roleRates,
    form,
    selectedRateCard,
    selectedRateCardVersion,
    rateCardDetails,
    rateCardVersions,
    onUpdateRateCardVersion,
    onDrawerClose,
  });

  const newRateCardVersionHandler = useCallback(() => {
    const newRateCardVersion = createNewRateCardVersion(
      getActiveOrRecentRateCardVersion(),
    );

    setSelectedRateCardVersion(newRateCardVersion);
    setRateCardVersions((prev) => [newRateCardVersion, ...prev]);
  }, [createNewRateCardVersion, getActiveOrRecentRateCardVersion]);

  const enableEditMode = useCallback(() => {
    setIsEditMode(true);
  }, []);

  const disableEditMode = useCallback(() => {
    resetToOriginalRoleRates();
    setIsEditMode(false);
  }, [resetToOriginalRoleRates]);

  const onRateCardVersionCreate = useCallback(
    (newRateCardVersion: RateCardVersion) => {
      setRateCardVersions((prev) => [
        ...prev.filter(({ rateCardVersionId }) => rateCardVersionId > 0),
        newRateCardVersion,
      ]);
      setSelectedRateCardVersion(newRateCardVersion);
    },
    [],
  );

  const onRateCardVersionUpdate = useCallback(
    (updatedRateCardVersion: RateCardVersion) => {
      disableEditMode();
      setRateCardVersions((prev) => [
        ...prev.filter(
          ({ rateCardVersionId }) =>
            rateCardVersionId !== updatedRateCardVersion.rateCardVersionId,
        ),
        updatedRateCardVersion,
      ]);
      setSelectedRateCardVersion(updatedRateCardVersion);
    },
    [disableEditMode],
  );

  const createNewRateCardHandler = useCallback(async () => {
    const rateCardPayload = getRateCardPayload();

    try {
      await createRateCardMutation(rateCardPayload);
      toastService({
        title: createSuccessToastTitle,
        message: createSuccessToastMessage,
      }).success();
      onDrawerClose();
      updateNewRateCardVersion(false);
    } catch (e) {
      toastService({
        title: createErrorToastTitle,
        message: createErrorToastMessage,
      }).error();
    }
  }, [
    createErrorToastMessage,
    createErrorToastTitle,
    createRateCardMutation,
    createSuccessToastMessage,
    createSuccessToastTitle,
    getRateCardPayload,
    onDrawerClose,
    updateNewRateCardVersion,
  ]);

  const {
    createNewRateCardVersionHandler,
    isRateCardVersionCreationInProgress,
  } = useRateCardVersionCreate({
    selectedRateCard,
    form,
    getRateCardVersionPayload,
    updateNewRateCardVersion,
    onRateCardVersionCreate,
  });

  const { deleteRateCardVersionHandler, isRateCardVersionDeletionInProgress } =
    useRateCardVersionDelete({
      selectedRateCardVersion,
      form,
    });

  const { updateRateCardVersionHandler, isRateCardVersionUpdationInProgress } =
    useRateCardVersionUpdate({
      selectedRateCard,
      form,
      getRateCardVersionPayload,
      onRateCardVersionUpdate,
    });

  useEffect(() => {
    if (selectedRateCard == null) {
      setSelectedRateCardVersion(createNewRateCardVersion());
    }
  }, [createNewRateCardVersion, selectedRateCard]);

  useEffect(() => {
    if (rateCardDetails == null) {
      return;
    }
    setRateCardVersions(rateCardDetails.rateCardVersions);
    setSelectedRateCardVersion(getActiveOrRecentRateCardVersion());
  }, [getActiveOrRecentRateCardVersion, rateCardDetails]);

  useEffect(() => {
    if (isNewRateCardVersion) {
      newRateCardVersionHandler();
    }
  }, [isNewRateCardVersion, newRateCardVersionHandler]);

  useEffect(() => {
    resetToOriginalRoleRates();
  }, [resetToOriginalRoleRates]);

  return (
    <CustomLayoutDrawer
      placement="right"
      visible={isVisible}
      width={
        selectedRateCard == null ? DRAWER_WIDTH : DRAWER_WIDTH + SIDER_WIDTH
      }
      mask
      onClose={closeDrawerHandler}
      closeIcon={<Close16 />}
      destroyOnClose
    >
      <StyledDrawerWithSider>
        {roleLookup == null ||
        userLookup == null ||
        (selectedRateCard != null && rateCardVersions.length === 0) ||
        selectedRateCardVersion == null ? (
          <LoaderWrapper>
            <Loading size="large" />
          </LoaderWrapper>
        ) : (
          <>
            <RateCardVersionsSider
              siderWidth={SIDER_WIDTH}
              selectedRateCard={selectedRateCard}
              rateCardVersions={rateCardVersions}
              selectedRateCardVersion={selectedRateCardVersion}
              isUnsavedRateCardVersionPresent={isUnsavedRateCardVersionPresent}
              updateSelectedRateCardVersion={updateSelectedRateCardVersion}
              newRateCardVersionHandler={newRateCardVersionHandler}
            />
            <StyledCustomDrawerContent>
              <CustomLayoutDrawerContent.Title>
                <RateCardsDrawerHeader
                  title={
                    selectedRateCard?.rateCardName ?? (
                      <FormattedMessage id="settings.rate_cards.drawer.title" />
                    )
                  }
                />
              </CustomLayoutDrawerContent.Title>
              <CustomLayoutDrawerContent.Content>
                {isRateCardDetailsLoading ? (
                  <LoaderWrapper>
                    <Loading size="large" />
                  </LoaderWrapper>
                ) : (
                  <RateCardsForm
                    form={form}
                    account={account}
                    roleRates={roleRates}
                    selectedRateCard={selectedRateCard}
                    selectedRateCardVersion={selectedRateCardVersion}
                    rateCardVersions={rateCardVersions}
                    isCurrentRateCardVersionReadOnly={
                      isCurrentRateCardVersionReadOnly
                    }
                    isEditMode={isEditMode}
                    roleLookup={roleLookup}
                    updateRoleRate={updateRoleRate}
                    deleteRoleRate={deleteRoleRate}
                    setCanSubmit={setCanSubmit}
                  />
                )}
              </CustomLayoutDrawerContent.Content>
              <CustomLayoutDrawerContent.Footer>
                <RateCardsDrawerFooter
                  canSubmit={canSubmit}
                  userLookup={userLookup}
                  selectedRateCard={selectedRateCard}
                  selectedRateCardVersion={selectedRateCardVersion}
                  isCurrentRateCardVersionReadOnly={
                    isCurrentRateCardVersionReadOnly
                  }
                  isEditMode={isEditMode}
                  enableEditMode={enableEditMode}
                  disableEditMode={disableEditMode}
                  onCreateNewRateCard={createNewRateCardHandler}
                  isRateCardCreationInProgress={isRateCardCreationInProgress}
                  onCreateNewRateCardVersion={createNewRateCardVersionHandler}
                  isRateCardVersionCreationInProgress={
                    isRateCardVersionCreationInProgress
                  }
                  onUpdateRateCardVersion={updateRateCardVersionHandler}
                  isRateCardVersionUpdationInProgress={
                    isRateCardVersionUpdationInProgress
                  }
                  onDeleteRateCardVersion={deleteRateCardVersionHandler}
                  isRateCardVersionDeletionInProgress={
                    isRateCardVersionDeletionInProgress
                  }
                  onClose={closeDrawerHandler}
                />
              </CustomLayoutDrawerContent.Footer>
            </StyledCustomDrawerContent>
          </>
        )}
      </StyledDrawerWithSider>
    </CustomLayoutDrawer>
  );
};
